import React from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { CHANNEL_LABELS } from '@/lib/crmMacros';

export function ChannelTag({ channel }) {
  return (
    <span className="inline-block border border-white/10 text-white/50 text-[9px] font-bold tracking-widest px-2 py-1">
      {(CHANNEL_LABELS[channel] || 'Email').toUpperCase()}
    </span>
  );
}

export function ActiveToggle({ on, disabled, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      aria-pressed={on}
      aria-label={on ? 'Active' : 'Inactive'}
      className={`w-10 h-5 rounded-full relative transition-colors disabled:opacity-40 ${
        on ? 'bg-primary' : 'bg-white/10'
      }`}
    >
      <span
        className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${
          on ? 'left-5' : 'left-0.5'
        }`}
      />
    </button>
  );
}

// One canned response: a table row on desktop, a card on mobile.
export default function MacroRow({ macro, busy, onEdit, onToggle, onDelete }) {
  const active = macro.active !== false;

  const actions = (
    <>
      <button
        type="button"
        onClick={() => onEdit(macro)}
        aria-label={`Edit ${macro.name}`}
        className="text-white/40 hover:text-white transition-colors"
      >
        <Pencil className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={() => onDelete(macro)}
        aria-label={`Delete ${macro.name}`}
        className="text-white/40 hover:text-red-400 transition-colors"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </>
  );

  return (
    <div className="border border-white/5 bg-[#111115]">
      <div className="hidden md:grid grid-cols-12 gap-3 items-center px-4 py-3">
        <div className="col-span-4 min-w-0">
          <p className="text-white text-xs font-bold truncate">{macro.name}</p>
          {macro.subject && (
            <p className="text-white/30 text-[10px] truncate mt-0.5">{macro.subject}</p>
          )}
        </div>
        <div className="col-span-2 text-white/50 text-[11px] truncate">
          {macro.category || 'General'}
        </div>
        <div className="col-span-2">
          <ChannelTag channel={macro.channel} />
        </div>
        <div className="col-span-1 text-white/40 text-[11px]">
          {Number(macro.usage_count || 0)}
        </div>
        <div className="col-span-2">
          <ActiveToggle on={active} disabled={busy} onToggle={() => onToggle(macro)} />
        </div>
        <div className="col-span-1 flex items-center justify-end gap-3">{actions}</div>
      </div>

      <div className="md:hidden p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-white text-xs font-bold">{macro.name}</p>
            <p className="text-white/30 text-[10px] mt-0.5">{macro.category || 'General'}</p>
          </div>
          <div className="flex items-center gap-3 shrink-0 pt-0.5">{actions}</div>
        </div>
        <div className="flex items-center justify-between gap-3 mt-3">
          <div className="flex items-center gap-3">
            <ChannelTag channel={macro.channel} />
            <span className="text-white/30 text-[10px]">
              {Number(macro.usage_count || 0)} use{Number(macro.usage_count || 0) === 1 ? '' : 's'}
            </span>
          </div>
          <ActiveToggle on={active} disabled={busy} onToggle={() => onToggle(macro)} />
        </div>
      </div>
    </div>
  );
}